/**
 * Google OAuth 2.0 Service
 * Handles OAuth flow for Google Business Profile API
 */

const GOOGLE_OAUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

// Scopes necesarios para Google Business Profile
const SCOPES = [
  'https://www.googleapis.com/auth/business.manage',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile'
].join(' ');

/**
 * Inicia el flujo OAuth abriendo una ventana flotante de Google
 * @param {string} clientId - ID del cliente desde .env
 * @param {string} redirectUri - URI de redirección
 * @param {string} state - Estado para seguridad (incluye clientId de la plataforma)
 */
export const initiateGoogleOAuth = (clientId, redirectUri, state) => {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: SCOPES,
    access_type: 'offline', // Para obtener refresh token
    prompt: 'consent', // Fuerza pantalla de consentimiento para obtener refresh token
    state: state // Incluye info del cliente para saber a quien asociar los tokens
  });

  const authUrl = `${GOOGLE_OAUTH_URL}?${params.toString()}`;

  // Configuración de la ventana flotante
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popupFeatures = `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`;

  // Abre ventana flotante para autorización de Google
  const popup = window.open(authUrl, 'GoogleOAuth', popupFeatures);

  if (popup) {
    popup.focus();
  }

  return popup;
};

/**
 * Extrae el código de autorización de la URL después de la redirección
 * @returns {Object} { code, state, error }
 */
export const extractAuthorizationCode = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description')
  };
};

/**
 * Intercambia el código de autorización por tokens (debe hacerse en backend)
 * Esta es una función de referencia - LA LLAMADA REAL DEBE SER AL BACKEND
 * @param {string} code - Código de autorización
 * @param {string} clientId - Client ID
 * @param {string} clientSecret - Client Secret (NUNCA exponer en frontend)
 * @param {string} redirectUri - Redirect URI
 */
export const exchangeCodeForTokens = async (code, apiEndpoint, clientId) => {
  try {
    // Llamada al backend para intercambiar código por tokens
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code,
        clientId, // ID del cliente de tu plataforma (no Google)
        redirectUri: process.env.REACT_APP_GOOGLE_OAUTH_REDIRECT_URI
      })
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for tokens');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    throw error;
  }
};

/**
 * Refresca el access token usando el refresh token (debe hacerse en backend)
 * @param {string} refreshToken - Refresh token
 * @param {string} apiEndpoint - Endpoint del backend
 */
export const refreshAccessToken = async (
  refreshToken,
  apiEndpoint,
  clientId
) => {
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        refreshToken,
        clientId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to refresh access token');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    throw error;
  }
};

/**
 * Obtiene las ubicaciones de Google Business Profile del usuario
 * @param {string} apiEndpoint - Endpoint del backend
 * @param {string} clientId - ID del cliente de la plataforma
 */
export const getBusinessLocations = async (apiEndpoint, clientId) => {
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ clientId })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch business locations');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching business locations:', error);
    throw error;
  }
};

/**
 * Guarda la ubicación seleccionada asociada al cliente
 * @param {string} apiEndpoint - Endpoint del backend
 * @param {string} clientId - ID del cliente de la plataforma
 * @param {Object} location - Datos de la ubicación seleccionada
 */
export const saveSelectedLocation = async (apiEndpoint, clientId, location) => {
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ clientId, location })
    });

    if (!response.ok) {
      throw new Error('Failed to save location');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error saving location:', error);
    throw error;
  }
};
